import { bindInternalInputPotential, bindMaterializedResource, setWorkflowStateEntry, } from '../mutableWorkflowOverlay.js';
import { isLoopFeedbackBindingSpecCompatible } from '../toolFilters.js';
import { extractResourceTypeHandleFromRole } from '../workflowExtraction.js';
import { buildToolStepByKeyMapForThread } from './macroStepHelpers.js';
export const makeLoopLineageKey = (threadIndex, rootLoopStepKey) => `${threadIndex}:${rootLoopStepKey}`;
export function upsertLoopLineageEntry(loopLineageEntryByKey, loopLineageKey, threadIndex, rootLoopStepKey, loopKind, whatSourceOutputAddress, whenStepKey) {
    const existingLoopLineageEntry = loopLineageEntryByKey[loopLineageKey];
    return {
        ...loopLineageEntryByKey,
        [loopLineageKey]: {
            threadIndex,
            rootLoopStepKey,
            loopKind,
            rootWhatOutputAddress: existingLoopLineageEntry?.rootWhatOutputAddress ?? whatSourceOutputAddress,
            rootWhenToolStepKey: existingLoopLineageEntry?.rootWhenToolStepKey ?? whenStepKey,
            latestWhatOutputAddress: whatSourceOutputAddress,
        },
    };
}
export function resolveWhenBindings(workflowState, whenTool, whenToolStep, nodeName) {
    const whenInputRoleBindingSpec = whenToolStep.whenInputRoleBindingSpec;
    if (!whenInputRoleBindingSpec) {
        throw new Error(`[${nodeName}] Missing whenInputRoleBindingSpec on when toolStep ${whenToolStep.stepKey}`);
    }
    const { dynamicInputRoleNames, staticInputRoleNames, } = whenInputRoleBindingSpec;
    if (dynamicInputRoleNames.length !== 1) {
        throw new Error(`[${nodeName}] Expected exactly one dynamic input role on when toolStep ${whenToolStep.stepKey}; got ${dynamicInputRoleNames.length}`);
    }
    if (staticInputRoleNames.length !== 1) {
        throw new Error(`[${nodeName}] Expected exactly one static input role on when toolStep ${whenToolStep.stepKey}; got ${staticInputRoleNames.length}`);
    }
    const dynamicRoleName = dynamicInputRoleNames[0];
    const staticRoleName = staticInputRoleNames[0];
    if (!dynamicRoleName || !staticRoleName) {
        throw new Error(`[${nodeName}] Missing dynamic/static role name on when toolStep ${whenToolStep.stepKey}`);
    }
    if (dynamicRoleName === staticRoleName) {
        throw new Error(`[${nodeName}] Dynamic and static input roles must be distinct on when toolStep ${whenToolStep.stepKey}`);
    }
    const inputBindings = whenToolStep.roleBindingSpec.inputBindings;
    if (!inputBindings.includes(dynamicRoleName)) {
        throw new Error(`[${nodeName}] Dynamic role '${dynamicRoleName}' is not present in roleBindingSpec.inputBindings for when toolStep ${whenToolStep.stepKey}`);
    }
    if (!inputBindings.includes(staticRoleName)) {
        throw new Error(`[${nodeName}] Static role '${staticRoleName}' is not present in roleBindingSpec.inputBindings for when toolStep ${whenToolStep.stepKey}`);
    }
    const dynamicRole = whenTool.roleSpec.inputRoleByName[dynamicRoleName];
    if (!dynamicRole) {
        throw new Error(`[${nodeName}] Missing roleSpec for '${dynamicRoleName}' on tool '${whenTool.handle}'`);
    }
    const dynamicRoleTypeHandle = extractResourceTypeHandleFromRole(dynamicRole);
    if (!dynamicRoleTypeHandle) {
        throw new Error(`[${nodeName}] Input role '${dynamicRoleName}' on tool '${whenTool.handle}' is not resource-backed`);
    }
    const originalStaticEntry = workflowState?.[whenToolStep.stepKey]?.[staticRoleName];
    if (!originalStaticEntry || originalStaticEntry.workflowStateEntryKind !== 'materializedResource') {
        throw new Error(`[${nodeName}] Expected materialized static input at (${whenToolStep.stepKey}, ${staticRoleName})`);
    }
    const originalDynamicEntry = workflowState?.[whenToolStep.stepKey]?.[dynamicRoleName];
    return {
        dynamicRoleName,
        staticRoleName,
        dynamicRoleTypeHandle,
        originalStaticEntry,
        originalDynamicEntry,
    };
}
export function bindCloneWhatToolStepInput(currentWorkflowState, cloneWhatToolStep, whatSourceOutputAddress, loopFeedbackBindingSpec, threadSteps, cloneToolSteps, toolMap, nodeName) {
    const changedToolStepKeys = new Set();
    const toolStepByKey = buildToolStepByKeyMapForThread(threadSteps, cloneToolSteps);
    const bindingContext = {
        toolStepByKey,
        toolMap,
    };
    const whatTool = toolMap.get(cloneWhatToolStep.toolHandle);
    if (!whatTool) {
        throw new Error(`[${nodeName}] Tool '${cloneWhatToolStep.toolHandle}' not found in toolMap`);
    }
    if (!isLoopFeedbackBindingSpecCompatible(whatTool, loopFeedbackBindingSpec)) {
        throw new Error(`[${nodeName}] Invalid loop feedback binding for Tool '${whatTool.handle}': '${loopFeedbackBindingSpec.sourceOutputRoleName}' -> '${loopFeedbackBindingSpec.targetInputRoleName}'`);
    }
    if (!cloneWhatToolStep.roleBindingSpec.inputBindings.includes(loopFeedbackBindingSpec.targetInputRoleName)) {
        throw new Error(`[${nodeName}] Loop feedback target '${loopFeedbackBindingSpec.targetInputRoleName}' is missing from ToolStep '${cloneWhatToolStep.stepKey}'`);
    }
    if (!cloneWhatToolStep.roleBindingSpec.outputBindings.includes(loopFeedbackBindingSpec.sourceOutputRoleName)) {
        throw new Error(`[${nodeName}] Loop feedback source '${loopFeedbackBindingSpec.sourceOutputRoleName}' is missing from ToolStep '${cloneWhatToolStep.stepKey}'`);
    }
    if (whatSourceOutputAddress.roleName !== loopFeedbackBindingSpec.sourceOutputRoleName) {
        throw new Error(`[${nodeName}] Loop feedback source address role '${whatSourceOutputAddress.roleName}' does not match '${loopFeedbackBindingSpec.sourceOutputRoleName}'`);
    }
    const currentWhatEntries = currentWorkflowState[whatSourceOutputAddress.stepKey] ?? {};
    let nextWorkflowState = currentWorkflowState;
    for (const inputRoleName of cloneWhatToolStep.roleBindingSpec.inputBindings) {
        if (inputRoleName === loopFeedbackBindingSpec.targetInputRoleName)
            continue;
        const currentEntry = currentWhatEntries[inputRoleName];
        if (currentEntry === undefined) {
            throw new Error(`[${nodeName}] Missing retained input '${inputRoleName}' on current What ToolStep '${whatSourceOutputAddress.stepKey}'`);
        }
        nextWorkflowState = setWorkflowStateEntry(nextWorkflowState, {
            stepKey: cloneWhatToolStep.stepKey,
            roleName: inputRoleName,
        }, currentEntry);
    }
    nextWorkflowState = bindInternalInputPotential(nextWorkflowState, {
        stepKey: cloneWhatToolStep.stepKey,
        roleName: loopFeedbackBindingSpec.targetInputRoleName,
    }, whatSourceOutputAddress, bindingContext);
    changedToolStepKeys.add(cloneWhatToolStep.stepKey);
    return {
        nextWorkflowState,
        changedToolStepKeys,
        bindingContext,
    };
}
export function getLoopFeedbackSourceOutputAddress(whatToolStep, loopFeedbackBindingSpec, nodeName) {
    if (!whatToolStep.roleBindingSpec.outputBindings.includes(loopFeedbackBindingSpec.sourceOutputRoleName)) {
        throw new Error(`[${nodeName}] Loop feedback source '${loopFeedbackBindingSpec.sourceOutputRoleName}' is missing from ToolStep '${whatToolStep.stepKey}'`);
    }
    return {
        stepKey: whatToolStep.stepKey,
        roleName: loopFeedbackBindingSpec.sourceOutputRoleName,
    };
}
export function bindCloneWhenToolStepStaticRole(currentWorkflowState, cloneWhenToolStep, staticRoleName, originalStaticEntry) {
    const nextWorkflowState = bindMaterializedResource(currentWorkflowState, {
        stepKey: cloneWhenToolStep.stepKey,
        roleName: staticRoleName,
    }, originalStaticEntry);
    return {
        nextWorkflowState,
        changedToolStepKey: cloneWhenToolStep.stepKey,
    };
}
